import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetAllTemplateMarketsQueryResponse, GetAllTemplateMarketsPathParams, GetAllTemplateMarketsQueryParams, GetAllTemplateMarkets400 } from "../types/GetAllTemplateMarkets";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetAllTemplateMarketsClient = typeof client<GetAllTemplateMarketsQueryResponse, GetAllTemplateMarkets400, never>;
type GetAllTemplateMarkets = {
    data: GetAllTemplateMarketsQueryResponse;
    error: GetAllTemplateMarkets400;
    request: never;
    pathParams: GetAllTemplateMarketsPathParams;
    queryParams: GetAllTemplateMarketsQueryParams;
    headerParams: never;
    response: GetAllTemplateMarketsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetAllTemplateMarketsClient>[0]>;
        return: Awaited<ReturnType<GetAllTemplateMarketsClient>>;
    };
};
export const getAllTemplateMarketsQueryKey = (clientCode: GetAllTemplateMarketsPathParams["clientCode"], params?: GetAllTemplateMarkets["queryParams"]) => [{ url: "/api/clients/:clientCode/markets/templates", params: { clientCode: clientCode } }, ...(params ? [params] : [])] as const;
export type GetAllTemplateMarketsQueryKey = ReturnType<typeof getAllTemplateMarketsQueryKey>;
export function getAllTemplateMarketsQueryOptions(clientCode: GetAllTemplateMarketsPathParams["clientCode"], params?: GetAllTemplateMarkets["queryParams"], options: GetAllTemplateMarkets["client"]["parameters"] = {}) {
    const queryKey = getAllTemplateMarketsQueryKey(clientCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllTemplateMarkets["data"], GetAllTemplateMarkets["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/templates`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all template markets that can be used to create new, client
specific markets.
 * @summary Get all templates
 * @link /api/clients/:clientCode/markets/templates
 */
export function useGetAllTemplateMarkets<TData = GetAllTemplateMarkets["response"], TQueryData = GetAllTemplateMarkets["response"], TQueryKey extends QueryKey = GetAllTemplateMarketsQueryKey>(clientCode: GetAllTemplateMarketsPathParams["clientCode"], params?: GetAllTemplateMarkets["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetAllTemplateMarkets["response"], GetAllTemplateMarkets["error"], TData, TQueryData, TQueryKey>>;
    client?: GetAllTemplateMarkets["client"]["parameters"];
} = {}): UseQueryResult<TData, GetAllTemplateMarkets["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllTemplateMarketsQueryKey(clientCode, params);
    const query = useQuery({
        ...getAllTemplateMarketsQueryOptions(clientCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetAllTemplateMarkets["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getAllTemplateMarketsSuspenseQueryKey = (clientCode: GetAllTemplateMarketsPathParams["clientCode"], params?: GetAllTemplateMarkets["queryParams"]) => [{ url: "/api/clients/:clientCode/markets/templates", params: { clientCode: clientCode } }, ...(params ? [params] : [])] as const;
export type GetAllTemplateMarketsSuspenseQueryKey = ReturnType<typeof getAllTemplateMarketsSuspenseQueryKey>;
export function getAllTemplateMarketsSuspenseQueryOptions(clientCode: GetAllTemplateMarketsPathParams["clientCode"], params?: GetAllTemplateMarkets["queryParams"], options: GetAllTemplateMarkets["client"]["parameters"] = {}) {
    const queryKey = getAllTemplateMarketsSuspenseQueryKey(clientCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllTemplateMarkets["data"], GetAllTemplateMarkets["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/templates`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all template markets that can be used to create new, client
specific markets.
 * @summary Get all templates
 * @link /api/clients/:clientCode/markets/templates
 */
export function useGetAllTemplateMarketsSuspense<TData = GetAllTemplateMarkets["response"], TQueryKey extends QueryKey = GetAllTemplateMarketsSuspenseQueryKey>(clientCode: GetAllTemplateMarketsPathParams["clientCode"], params?: GetAllTemplateMarkets["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetAllTemplateMarkets["response"], GetAllTemplateMarkets["error"], TData, TQueryKey>>;
    client?: GetAllTemplateMarkets["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetAllTemplateMarkets["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllTemplateMarketsSuspenseQueryKey(clientCode, params);
    const query = useSuspenseQuery({
        ...getAllTemplateMarketsSuspenseQueryOptions(clientCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetAllTemplateMarkets["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}